/* jshint esnext:true */
/* global window */
/// <reference types="doubleclick-gpt" />
import difference from 'lodash-es/difference';
import flatten from 'lodash-es/flatten';
import forEach from 'lodash-es/forEach';
import intersection from 'lodash-es/intersection';
import mapValues from 'lodash-es/mapValues';
import union from 'lodash-es/union';
import uniqBy from 'lodash-es/uniqBy';
import hbSizes, { ScreenSizes, SizeMapping } from 'mk/ox/hbSizes';
import pbjs from 'prebid.js';
import 'prebid.js/modules/adformBidAdapter'; // tslint:disable-line
// CeMi 28.5.2019: not used at the moment: import 'prebid.js/modules/criteoBidAdapter'; // tslint:disable-line
import { slugify } from '../common/utils';
import { ACTIVE_SERVER_ID } from '../settings';
import { removeAdSlotClass, removeBodyLayout, scaleMailAd, setBodyLayout } from './dom';
import {
    AdUnitDefinition,
    all as hbAdUnitsAll,
    Collapse,
    isBrandingDesktopAdUnit,
    isMailAdUnit,
    mail,
    mailCtaButton,
} from './hbAdUnits';

// kolko sa caka, aby externe systemy odpovedali s ponukou reklamy
export const PREBID_TIMEOUT = 700;

// globalna queue pre Doubleclick prikazy
// noinspection ES6ConvertVarToLetConst
let googletag;

// referencie na sloty z Doubleclicku  (zoneName ->  GPT slot object)
const GPT_SLOTS: {[zoneName: string]: googletag.Slot} = {};

// http://prebid.org/dev-docs/adunit-reference.html
interface AdUnit {
    gptid: string;
    code: string;
    bids: any[];
    sizeMapping: SizeMapping;
    mediaTypes: {
        banner: {
            sizes: number[][];
        };
    };
    labelAny: string[];
    collapse?: Collapse;
}

export const AD_UNITS: {[adUnitName: string]: AdUnit} = mapValues(
    hbAdUnitsAll,
    (adUnitDef: AdUnitDefinition, adUnitName: string) => {
        // copy server specific parts to parent
        const adUnit: AdUnit = {
            ...adUnitDef,
            ...adUnitDef[ACTIVE_SERVER_ID],
        };
        adUnit.code = `slot-${slugify(adUnitName)}`;

        // remove all server parts
        delete adUnit[201];
        delete adUnit[202];
        delete adUnit[207];

        // Convert sizeMapping to sizes
        adUnit.mediaTypes = {
            banner: {
                // collect all banner sizes defined for each screen size
                sizes: uniqBy(
                    flatten(adUnit.sizeMapping.map((ss: ScreenSizes) => ss.sizesSupported)).
                    filter((s: googletag.SingleSize) => s !== 'fluid') as googletag.SingleSizeArray[],
                    (i: googletag.SingleSizeArray) => i.join('x'),
                ),
            },
        };

        adUnit.labelAny = adUnit.labelAny || flatten(
            adUnit.sizeMapping.map((ss: ScreenSizes) => ss.sizesSupported.length > 0 ? ss.labels : []),
        );

        return adUnit;
    },
);

// mapovanie cien na Line Items v DFP
// http://prebid.org/dev-docs/publisher-api-reference.html#module_pbjs.setPriceGranularity
export const PREBID_PRICE_BUCKETS = {
    buckets: [{
        min: 0,
        max: 1,
        increment: 0.05,
    }, {
        min: 1,
        max: 2,
        increment: 0.1,
    }, {
        min: 2,
        max: 5,
        increment: 0.5,
    }, {
        min: 100,
        max: 100,
        increment: 1,
    }],
};

/**
 * zones, that are currenly on page
 */
let ZONES_CURRENTLY_ON_PAGE: string[] = [];

export function hbZonesCurrentlyOnPage() {
    return ZONES_CURRENTLY_ON_PAGE.slice(0); // Clone so that this variable is not mutated outside
}

declare const window;

let hbInitialized: boolean = false;

// noinspection JSUnusedGlobalSymbols - called from jinja template
export function hbInit() {
    if (hbInitialized) {
        return;
    }
    hbInitialized = true;

    pbjs.processQueue();

    googletag = window.googletag || {};
    googletag.cmd = googletag.cmd || [];
    window.googletag = googletag;

    googletag.cmd.push(() => {
        googletag.pubads().disableInitialLoad();

        googletag.pubads().enableSingleRequest();
        // Global collapse setting (can be overriden per slot)
        // Ad slots will stay empty most of the time. So, collapse all
        // divs on the page BEFORE the browser fetches any ads
        // https://support.google.com/dfp_premium/answer/3072674?hl=en
        googletag.pubads().collapseEmptyDivs(true);
        googletag.enableServices();

        googletag.pubads().addEventListener('slotRenderEnded', (event: googletag.events.SlotRenderEndedEvent) => {
            const code = event.slot.getSlotElementId();
            const element = document.getElementById(event.slot.getSlotElementId());

            if (isBrandingDesktopAdUnit(code)) {
                let hasBranding;
                // completed loading of branding ad unit
                if (!event.isEmpty && event.size && Array.isArray(event.size)) {
                    // set page to layout according to the size of creative
                    // size '1x1' is returned from prebid line item. In that case we know,
                    // Adform is configured to only return branding (2000x1400) creatives
                    const layout = {2000: 'branding', 970: 'billboard', 1: 'branding'}[event.size[0]];
                    hasBranding = layout === 'branding';
                    setBodyLayout(layout);
                } else {
                    // reset to normal page layout to normal, we do not have branding
                    removeBodyLayout();
                    hasBranding = false;
                }

                // ak nemame branding, pozri ci je v stranke bocny halfpage a zobraz ho
                if (!hasBranding) {
                    // convert nodelist to array and select zone-name value
                    let nbZones = [].map.call(
                        document.querySelectorAll('[data-component="AdSlotOffsite"]'),
                        (elem) => elem.getAttribute('data-zone-name'),
                    );
                    if (nbZones.length) {
                        nbZones = nbZones.concat(ZONES_CURRENTLY_ON_PAGE);
                        hbShowAds(nbZones, null, false);
                    }
                }
            }

            if (isMailAdUnit(code)) {
                removeAdSlotClass(code, 'not-loaded-countdown');
                const isFluid = (
                    event.size && Array.isArray(event.size) && event.size.length === 2 &&
                    event.size[0] === 0  && event.size[1] === 0
                );
                if (!event.isEmpty && !isFluid) {
                    scaleMailAd(event.slot.getSlotElementId());
                }
            }
        });
    });

    pbjs.que.push(() => {
        // nastav cenove hladiny 0.50, 1.00, 1.50, ..., 5.00
        // (rovnaka schema musi byt nakonfiguravna v DfP)
        pbjs.setConfig({
            sizeConfig: hbSizes,
            priceGranularity: PREBID_PRICE_BUCKETS,
            bidderTimeout: PREBID_TIMEOUT,
        });
    });
}

export interface ZoneTargeting {
    [key: string]: string;
}

// noinspection JSUnusedGlobalSymbols - called from jinja template
export function hbShowAds(
    hbZonesToDisplay: string[],
    hbZonesTargeting: {[zoneName: string]: ZoneTargeting} = null,
    isNewPage: boolean = true,
) {
    let zonesInsert = difference(hbZonesToDisplay, ZONES_CURRENTLY_ON_PAGE);
    let zonesRemove = difference(ZONES_CURRENTLY_ON_PAGE, hbZonesToDisplay);

    let zonesRefresh = [];
    if (isNewPage) {
        let zonesKeep = intersection(ZONES_CURRENTLY_ON_PAGE, hbZonesToDisplay);

        // we have to recreate mail adslot, to force reloading its ad
        const zonesRecreate = intersection(zonesKeep, union(Object.keys(mail), Object.keys(mailCtaButton)));
        if (zonesRecreate.length) {
            zonesInsert = union(zonesInsert, zonesRecreate);
            zonesRemove = union(zonesRemove, zonesRecreate);
            zonesKeep = difference(zonesKeep, zonesRecreate);
        }

        zonesRefresh = union(zonesInsert, zonesKeep);
    } else {
        zonesRefresh = zonesInsert;
    }

    ZONES_CURRENTLY_ON_PAGE = hbZonesToDisplay;

    // update ads
    unregisterZones(zonesRemove);
    registerZones(zonesInsert, hbZonesTargeting);

    refreshAds(zonesRefresh);
}

/**
 * Register given zones in Prebid.js and GPT
 *
 * @param hbZones list of zone names
 * @param hbZonesTargeting string with comma separated list of targeting keywords
 */
function registerZones(hbZones: string[], hbZonesTargeting: {[zoneName: string]: ZoneTargeting}) {
    if (!hbZones.length || !hbInitialized) {
        return;
    }

    googletag.cmd.push(() => {
        hbZones.forEach((zoneName) => {
            const adUnit = AD_UNITS[zoneName];

            // https://support.google.com/dfp_premium/answer/3423562?hl=en
            let gptSizeMappingBuilder: googletag.SizeMappingBuilder = googletag.sizeMapping();
            adUnit.sizeMapping.forEach((sm: ScreenSizes) => {
                const screenSize = [sm.minScreenWidth, 0];
                const slotSizes = sm.sizesSupported;
                gptSizeMappingBuilder = gptSizeMappingBuilder.addSize(screenSize, slotSizes);
            });
            const gptSizeMapping: googletag.SizeMappingArray = gptSizeMappingBuilder.build();

            GPT_SLOTS[zoneName] = googletag
                .defineSlot(adUnit.gptid, [], adUnit.code)
                .defineSizeMapping(gptSizeMapping)
                .addService(googletag.pubads());

            if (hbZonesTargeting && hbZonesTargeting[zoneName]) {
                forEach(hbZonesTargeting[zoneName], (targetingValue, targetingKey) => {
                    GPT_SLOTS[zoneName].setTargeting(targetingKey, targetingValue);
                });
            }

            // set per zone collapsing (before fetch/after fetch/never)
            // https://support.google.com/dfp_premium/answer/3072674?hl=en
            if (adUnit.collapse !== undefined) {
                switch (adUnit.collapse) {
                    case Collapse.NEVER:
                        GPT_SLOTS[zoneName].setCollapseEmptyDiv(false);
                        break;
                    case Collapse.AFTER_FETCH:
                        GPT_SLOTS[zoneName].setCollapseEmptyDiv(true);
                        break;
                    case Collapse.BEFORE_FETCH:
                        GPT_SLOTS[zoneName].setCollapseEmptyDiv(true, true);
                        break;
                    default:
                        // pass
                }
            }
        });
    });

    pbjs.que.push(() => {
        const adUnits = hbZones.map((zoneName) => {
            return AD_UNITS[zoneName];
        });

        pbjs.addAdUnits(adUnits);
    });

    googletag.cmd.push(() => {
        hbZones.forEach((zoneName) => {
            googletag.display(AD_UNITS[zoneName].code);
        });
    });
}

/**
 * Unregister given zones from Prebid.js and GPT
 *
 * @param hbZones list of zone names
 */
function unregisterZones(hbZones: string[]) {
    if (!hbZones.length || !hbInitialized) {
        return;
    }

    googletag.cmd.push(() => {
        const slots = hbZones.map((zoneName) => GPT_SLOTS[zoneName]);
        googletag.destroySlots(slots);
    });
}

function refreshAds(hbZones: string[]) {
    if (!hbZones.length || !hbInitialized) {
        return;
    }

    googletag.cmd.push(() => {
        pbjs.que.push(() => {
            const slots = hbZones.map((zoneName) => GPT_SLOTS[zoneName]);
            const slotsElemIds = slots.map((slot) => slot.getSlotElementId());

            let adserverRequestSent = false;

            function callDFP() {
                if (adserverRequestSent) {
                    return;
                }
                adserverRequestSent = true;

                // send request to DFP to display the best bid
                pbjs.setTargetingForGPTAsync(slotsElemIds);

                // if (_.indexOf(hbZones, 'Test - Billboard (Desktop)') >= 0) {
                //     const adUnitTargetings = pbjs.getAdserverTargeting(slotsElemIds);
                //     _.forEach(adUnitTargetings, (adUnitTargeting) => {
                //         if (adUnitTargeting.hb_bidder && adUnitTargeting.hb_pb) {
                //             window.ga(
                //                 'pbjsTracker.send', 'event',
                //                 'Prebid.js Bids', 'Sent to DFP',
                //                 adUnitTargeting.hb_bidder || 'unknown',
                //                 parseInt(adUnitTargeting.hb_pb, 10) * 100 || 0,
                //                 {nonInteraction: true},
                //             );
                //         }
                //     });
                // }

                // DFP compares the best bid with offers from ADX (Google's ad exchange)
                // and displays the priciest ad
                googletag.pubads().refresh(slots);
            }

            // call bidders
            pbjs.requestBids({
                adUnitCodes: slotsElemIds,
                bidsBackHandler: () => {
                    // print best bid to console
                    // var targetingParams = pbjs.getAdserverTargeting();
                    // console.log(JSON.stringify(targetingParams, null, 2));
                    callDFP();
                },
            });

            // if bidders do not complete within PREBID_TIMEOUT,
            // call DFP with the partial result
            setTimeout(() => {
                callDFP();
            }, PREBID_TIMEOUT);
        });
    });
}

// noinspection JSUnusedGlobalSymbols
export function hbGetZoneId(zoneName: string) {
    const adUnit = AD_UNITS[zoneName];
    if (!adUnit) {
        return null;
    }

    return adUnit.code;
}
